import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Wifi, Clock, ShoppingCart, Loader2, Check, Smartphone, QrCode, Share2, ArrowUpDown, TrendingDown, Database, Heart } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { useCurrency } from '../context/CurrencyContext';
import { DeviceCompatibilityChecker } from './DeviceCompatibility';
import PriceAlertBox from './PriceAlertBox';
import { NetworkAlertBanner } from './NetworkAlertBanner';
import { trackViewItem } from '../utils/analytics';
import { getLandmarkUrl } from '../utils/fallbackImages';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DATA_ORDER = ['1 GB', '2 GB', '3 GB', '5 GB', '10 GB', '20 GB', '30 GB', '50 GB', 'Unlimited'];
const DURATION_ORDER = ['1 Day', '2 Day', '3 Day', '4 Day', '5 Day', '6 Day', '7 Day', '8 Day', '9 Day', '10 Day', '11 Day', '12 Day', '13 Day', '14 Day', '15 Day', '16 Day', '17 Day', '18 Day', '19 Day', '20 Day', '25 Day', '30 Day', '60 Day', '90 Day'];

function sortByOrder(items, order) {
  return [...items].sort((a, b) => {
    const idxA = order.indexOf(a);
    const idxB = order.indexOf(b);
    if (idxA === -1 && idxB === -1) return a.localeCompare(b);
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;
    return idxA - idxB;
  });
}

export default function ProductDetail({ product, onBack, onAddToCart }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const isFav = isInWishlist(product.id);
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [planType, setPlanType] = useState('fixed');
  const [sortBy, setSortBy] = useState('default');
  const [imgSrc, setImgSrc] = useState('');

  useEffect(() => {
    const landmarkUrl = product.country_code ? getLandmarkUrl(product.country_code) : null;
    const flagUrl = product.flag_url || (product.country_code ? `https://flagcdn.com/w640/${product.country_code.toLowerCase()}.png` : '');
    setImgSrc(landmarkUrl || flagUrl);
    trackViewItem(product);
  }, [product]);

  useEffect(() => {
    const fetchVariations = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${product.id}/variations`);
        setVariations(res.data.variations || []);
      } catch (err) {
        console.error('Error fetching variations:', err);
      } finally {
        setLoading(false);
      }
    };
    if (product.type === 'variable') {
      fetchVariations();
    } else {
      setLoading(false);
    }
  }, [product]);

  const { fixedVariations, unlimitedVariations, dataOptions, durationOptions } = useMemo(() => {
    const fixed = variations.filter(v => v.data !== 'Unlimited');
    const unlimited = variations.filter(v => v.data === 'Unlimited');

    const fixedDataOpts = sortByOrder([...new Set(fixed.map(v => v.data))], DATA_ORDER);
    const fixedDurOpts = sortByOrder([...new Set(fixed.map(v => v.duration))], DURATION_ORDER);
    const unlimDurOpts = sortByOrder([...new Set(unlimited.map(v => v.duration))], DURATION_ORDER);

    return {
      fixedVariations: fixed,
      unlimitedVariations: unlimited,
      dataOptions: fixedDataOpts,
      durationOptions: planType === 'fixed' ? fixedDurOpts : unlimDurOpts
    };
  }, [variations, planType]);

  const displayVariations = useMemo(() => {
    if (planType === 'fixed') {
      return fixedVariations;
    }
    return unlimitedVariations;
  }, [planType, fixedVariations, unlimitedVariations]);

  const variationGrid = useMemo(() => {
    let grid = [];

    if (planType === 'unlimited') {
      const sorted = sortByOrder(
        [...new Set(unlimitedVariations.map(v => v.duration))],
        DURATION_ORDER
      );
      grid = sorted.map(dur => {
        const v = unlimitedVariations.find(x => x.duration === dur);
        return v ? { ...v, label: dur } : null;
      }).filter(Boolean);
    } else {
      const sortedData = sortByOrder([...new Set(fixedVariations.map(v => v.data))], DATA_ORDER);
      const sortedDur = sortByOrder([...new Set(fixedVariations.map(v => v.duration))], DURATION_ORDER);

      for (const data of sortedData) {
        for (const dur of sortedDur) {
          const v = fixedVariations.find(x => x.data === data && x.duration === dur);
          if (v) {
            grid.push({ ...v, label: `${data} / ${dur}` });
          }
        }
      }
    }

    // Apply sorting
    if (sortBy === 'price-asc') {
      grid.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (sortBy === 'price-desc') {
      grid.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (sortBy === 'data-asc') {
      grid.sort((a, b) => {
        const dataA = DATA_ORDER.indexOf(a.data);
        const dataB = DATA_ORDER.indexOf(b.data);
        if (dataA !== dataB) return dataA - dataB;
        return parseFloat(a.price) - parseFloat(b.price);
      });
    }

    return grid;
  }, [planType, fixedVariations, unlimitedVariations, sortBy]);

  const handleAddToCart = () => {
    if (!selectedVariation) return;
    const v = variations.find(x => x.id === selectedVariation);
    if (!v) return;

    const label = v.data === 'Unlimited'
      ? `Unlimited - ${v.duration}`
      : `${v.data} - ${v.duration}`;

    onAddToCart({
      id: v.id,
      name: `${product.name} (${label})`,
      price: v.price,
      product_id: v.id,
      parent_id: product.id,
      variation_label: label,
      package_type_id: v.id,
      country_code: product.country_code || '',
    });
  };

  const selectedV = variations.find(v => v.id === selectedVariation);
  const minPrice = variations.length > 0 
    ? Math.min(...variations.map(v => parseFloat(v.price))).toFixed(2)
    : parseFloat(product.price).toFixed(2);

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-white mb-8 flex items-center space-x-2 transition-colors"
          data-testid="product-detail-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('productDetail.backToShop')}</span>
        </button>

        <NetworkAlertBanner countryCode={product.country_code} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Product Image */}
          <div>
            <div className="rounded-2xl overflow-hidden border border-white/10 relative bg-gradient-to-br from-slate-800 to-slate-900">
              {(() => {
                const hasLandmark = product.country_code && getLandmarkUrl(product.country_code);
                return (<>
                  <img
                    src={imgSrc}
                    alt={product.name}
                    className={`w-full h-80 sm:h-96 object-cover ${hasLandmark ? 'opacity-80 scale-105' : 'opacity-40 blur-[2px] scale-110'}`}
                  />
                  <div className={`absolute inset-0 ${hasLandmark ? 'bg-gradient-to-t from-[#0f1419] via-[#0f1419]/40 to-transparent' : 'bg-gradient-to-t from-[#0f1419] via-[#0f1419]/50 to-transparent'}`} />
                </>);
              })()}
              {product.country_code && (
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <img
                    src={`https://flagcdn.com/w160/${product.country_code.toLowerCase()}.png`}
                    alt=""
                    className="w-16 h-12 rounded-lg shadow-lg object-cover border border-white/20"
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-white drop-shadow-lg">
                      {product.country_name || product.name?.replace('eSIM for ', '')}
                    </h2>
                    {product.categories?.[0] && (
                      <span className="text-teal-400 text-sm">{product.categories[0].name}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Features */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { icon: Check, text: t('productDetail.affordablePlans') + ' ' + formatPrice(minPrice) },
                { icon: Smartphone, text: t('productDetail.keepNumber') },
                { icon: QrCode, text: t('productDetail.instantQR') },
                { icon: Share2, text: t('productDetail.shareableHotspot') }
              ].map((feat, i) => (
                <div key={i} className="flex items-center space-x-2 text-sm text-slate-400">
                  <feat.icon className="w-4 h-4 text-teal-400 flex-shrink-0" />
                  <span>{feat.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Product Info & Variations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-bold text-white font-outfit" data-testid="product-detail-name">
                {product.name}
              </h1>
              {user && (
                <button
                  onClick={() => toggleWishlist(product)}
                  className={`p-2.5 rounded-full transition-all duration-200 ${
                    isFav
                      ? 'bg-rose-500 text-white'
                      : 'bg-white/5 border border-white/10 text-slate-400 hover:text-rose-400 hover:border-rose-400/40'
                  }`}
                  data-testid="product-detail-wishlist"
                >
                  <Heart className={`w-5 h-5 ${isFav ? 'fill-current' : ''}`} />
                </button>
              )}
            </div>
            <p className="text-2xl font-bold text-teal-400 font-mono mb-6" data-testid="product-detail-price">
              {selectedV ? formatPrice(selectedV.price) : `${t('products.from')} ${formatPrice(minPrice)}`}
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
              </div>
            ) : product.type === 'variable' && variations.length > 0 ? (
              <>
                {/* Fixed / Unlimited Toggle */}
                {unlimitedVariations.length > 0 && fixedVariations.length > 0 && (
                  <div className="flex rounded-xl overflow-hidden border border-white/10 mb-6" data-testid="plan-type-toggle">
                    <button
                      onClick={() => { setPlanType('fixed'); setSelectedVariation(null); }}
                      className={`flex-1 py-3 text-sm font-semibold transition-all ${
                        planType === 'fixed'
                          ? 'bg-teal-500 text-black'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                      data-testid="plan-type-fixed"
                    >
                      {t('productDetail.fixed')}
                    </button>
                    <button
                      onClick={() => { setPlanType('unlimited'); setSelectedVariation(null); }}
                      className={`flex-1 py-3 text-sm font-semibold transition-all ${
                        planType === 'unlimited'
                          ? 'bg-teal-500 text-black'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                      data-testid="plan-type-unlimited"
                    >
                      {t('productDetail.unlimited')}
                    </button>
                  </div>
                )}

                {/* Sort Controls */}
                <div className="flex items-center gap-2 mb-4" data-testid="sort-controls">
                  <span className="text-slate-500 text-xs">{t('productDetail.sort')}:</span>
                  {[
                    { key: 'default', label: t('productDetail.default'), icon: ArrowUpDown },
                    { key: 'price-asc', label: t('productDetail.cheapest'), icon: TrendingDown },
                    { key: 'data-asc', label: t('productDetail.byData'), icon: Database }
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        sortBy === opt.key
                          ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40'
                          : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`sort-${opt.key}`}
                    >
                      <opt.icon className="w-3 h-3" />
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>

                {/* Variation Grid */}
                <div className="max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                  <div className="grid grid-cols-3 gap-2" data-testid="variation-grid">
                    {variationGrid.map(v => (
                      <button
                        key={v.id}
                        onClick={() => setSelectedVariation(v.id)}
                        className={`relative p-3 rounded-xl border text-left transition-all duration-200 ${
                          selectedVariation === v.id
                            ? 'bg-teal-500/15 border-teal-500 ring-1 ring-teal-500/50'
                            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                        }`}
                        data-testid={`variation-${v.id}`}
                      >
                        {selectedVariation === v.id && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-black" />
                          </div>
                        )}
                        <p className="text-white font-semibold text-sm">
                          {planType === 'unlimited' ? t('productDetail.unlimited') : v.data}
                        </p>
                        <p className="text-teal-400 font-bold font-mono text-sm mt-1">
                          {formatPrice(v.price)}
                        </p>
                        <p className="text-slate-500 text-xs mt-0.5">{v.duration}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Add to Cart */}
                <div className="mt-4">
                  <DeviceCompatibilityChecker compact={true} />
                </div>
                <PriceAlertBox product={product} />
                <div className="mt-4 sticky bottom-0">
                  <button
                    onClick={handleAddToCart}
                    disabled={!selectedVariation}
                    className="w-full bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-black font-bold py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] flex items-center justify-center space-x-2"
                    data-testid="add-variation-to-cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span>
                      {selectedVariation
                        ? `${t('productDetail.addToCart')} - ${formatPrice(selectedV?.price || 0)}`
                        : t('productDetail.selectPlan')}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              /* Simple product */
              <>
                <div className="mt-4">
                  <DeviceCompatibilityChecker compact={true} />
                </div>
                <PriceAlertBox product={product} />
                <div className="mt-4">
                  <button
                    onClick={() => onAddToCart(product)}
                    className="w-full bg-teal-500 hover:bg-teal-400 text-black font-bold py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] flex items-center justify-center space-x-2"
                    data-testid="add-simple-to-cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span>{t('productDetail.addToCart')} - {formatPrice(product.price)}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
